export interface ColunaRel {
	text: string
	value: string
	align?: string
}

export interface SecaoRel {
	titulo: string
	headers: ColunaRel[]
	itens: any[]
}

export interface BarraRel {
	label: string
	valor: string
	pct: number
}

export interface RelatorioDashboardParams {
	corPrimaria: string
	empresa: string
	periodoLabel: string
	lojasLabel: string
	geradoEm: string
	heroValor: string
	heroLabel: string
	kpis: { titulo: string; valor: string }[]
	barras: BarraRel[]
	secoes: SecaoRel[]
}

function clarear(hex: string, fator: number): string {
	const h = hex.replace('#', '')
	if (h.length !== 6) return hex
	const n = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16))
	const mix = (c: number) => Math.round(c + (255 - c) * fator)
	return `#${n.map(c => mix(c).toString(16).padStart(2, '0')).join('')}`
}

function esc(v: any): string {
	return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function gerarRelatorioDashboard(p: RelatorioDashboardParams) {
	const cor = p.corPrimaria || '#60269E'
	const accent = clarear(cor, 0.45)

	const kpisHtml = p.kpis.map(k => `
		<div class="kpi">
			<div class="kpi-num">${esc(k.valor)}</div>
			<div class="kpi-label">${esc(k.titulo)}</div>
		</div>`).join('')

	const barrasHtml = p.barras.length ? `
		<div class="seclabel">Faturamento por loja</div>
		<div class="bars">
			${p.barras.map(b => `
				<div class="bar-row">
					<div class="bar-name">${esc(b.label)}</div>
					<div class="bar-track"><div class="bar-fill" style="width:${Math.max(2, b.pct)}%"></div></div>
					<div class="bar-val">${esc(b.valor)}</div>
				</div>`).join('')}
		</div>` : ''

	const secoesHtml = p.secoes.filter(s => s.itens && s.itens.length).map(s => `
		<div class="seclabel">${esc(s.titulo)}</div>
		<table class="tbl">
			<thead><tr>${s.headers.map(h => `<th class="${h.align === 'end' ? 'r' : ''}">${esc(h.text)}</th>`).join('')}</tr></thead>
			<tbody>
				${s.itens.map(it => `<tr>${s.headers.map(h => `<td class="${h.align === 'end' ? 'r' : ''}">${esc(it[h.value])}</td>`).join('')}</tr>`).join('')}
			</tbody>
		</table>`).join('')

	const html = `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8"><title>Resumo do dashboard — ${esc(p.empresa)}</title>
<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
	* { margin:0; padding:0; box-sizing:border-box; }
	body { font-family:'Poppins',system-ui,sans-serif; color:#1d1c2b; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
	.cover { background:linear-gradient(135deg,#1b1530 0%,#0f0f1a 60%,#161126 100%); color:#fff; padding:64px 70px; min-height:720px; display:flex; flex-direction:column; }
	.brand { color:${accent}; font-size:13px; font-weight:600; letter-spacing:4px; }
	.cover h1 { font-size:64px; font-weight:800; line-height:1.05; margin-top:60px; }
	.cover h1 .ac { color:${accent}; }
	.cover .sub { color:#a39fb8; font-size:18px; letter-spacing:2px; margin-top:14px; }
	.rule { width:64px; height:3px; background:${accent}; margin:36px 0; border-radius:2px; }
	.meta { display:flex; gap:60px; }
	.meta .label { color:#6f6a86; font-size:11px; letter-spacing:2px; font-weight:600; }
	.meta .val { color:#fff; font-size:15px; font-weight:600; margin-top:6px; }
	.hero { margin-top:auto; }
	.hero-num { font-size:88px; font-weight:800; color:${accent}; line-height:1; }
	.hero-label { color:#6f6a86; font-size:13px; letter-spacing:3px; font-weight:600; margin-top:6px; }
	.content { padding:48px 56px; background:#faf9fc; }
	.print-bar { text-align:center; margin-bottom:36px; }
	.print-btn { background:${cor}; color:#fff; border:none; border-radius:10px; padding:12px 26px; font-family:inherit; font-size:14px; font-weight:600; cursor:pointer; }
	.seclabel { color:${cor}; font-size:13px; font-weight:700; letter-spacing:2px; text-transform:uppercase; margin:34px 0 14px; }
	.kpis { display:flex; gap:16px; flex-wrap:wrap; }
	.kpi { flex:1; min-width:150px; background:#fff; border:1px solid #ece9f3; border-radius:14px; padding:20px 22px; }
	.kpi-num { font-size:30px; font-weight:700; color:${cor}; }
	.kpi-label { color:#8a86a0; font-size:12px; letter-spacing:1px; text-transform:uppercase; margin-top:6px; }
	.bars { background:#fff; border:1px solid #ece9f3; border-radius:14px; padding:22px 26px; }
	.bar-row { display:flex; align-items:center; gap:16px; margin:10px 0; }
	.bar-name { width:180px; font-size:13px; font-weight:500; }
	.bar-track { flex:1; height:14px; background:#f0eef6; border-radius:8px; overflow:hidden; }
	.bar-fill { height:100%; background:${cor}; border-radius:8px; }
	.bar-val { width:140px; text-align:right; font-size:13px; font-weight:600; }
	.tbl { width:100%; border-collapse:collapse; background:#fff; border:1px solid #ece9f3; border-radius:14px; overflow:hidden; }
	.tbl th { background:${cor}; color:#fff; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.5px; padding:10px 12px; text-align:left; }
	.tbl td { font-size:12px; padding:9px 12px; border-top:1px solid #f0eef6; }
	.tbl tr:nth-child(even) td { background:#faf9fc; }
	.tbl .r { text-align:right; }
	.foot { text-align:center; color:#a7a3b8; font-size:11px; margin-top:40px; }
	@media print {
		.print-bar { display:none; }
		.cover { page-break-after:always; min-height:96vh; }
		.seclabel, .kpi, .bars, .tbl { break-inside:avoid; }
		@page { size:A4; margin:0; }
		.content { padding:32px 36px; }
	}
</style></head>
<body>
	<section class="cover">
		<div class="brand">${esc(p.empresa)}</div>
		<h1>Resumo do<br><span class="ac">Dashboard</span></h1>
		<div class="sub">Visão geral de vendas</div>
		<div class="rule"></div>
		<div class="meta">
			<div><div class="label">PERÍODO</div><div class="val">${esc(p.periodoLabel)}</div></div>
			<div><div class="label">LOJAS</div><div class="val">${esc(p.lojasLabel)}</div></div>
			<div><div class="label">GERADO EM</div><div class="val">${esc(p.geradoEm)}</div></div>
		</div>
		<div class="hero">
			<div class="hero-num">${esc(p.heroValor)}</div>
			<div class="hero-label">${esc(p.heroLabel)}</div>
		</div>
	</section>
	<section class="content">
		<div class="print-bar"><button class="print-btn" onclick="window.print()">🖨 Imprimir / Salvar PDF</button></div>
		<div class="seclabel">Visão geral · ${esc(p.empresa)}</div>
		<div class="kpis">${kpisHtml}</div>
		${barrasHtml}
		${secoesHtml}
		<div class="foot">Gerado em ${esc(p.geradoEm)}</div>
	</section>
</body></html>`

	const win = window.open('', '_blank')
	if (!win) return
	win.document.open()
	win.document.write(html)
	win.document.close()
}
